import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  Switch,
  FormControlLabel,
  Divider
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  Code as CodeIcon
} from '@mui/icons-material';
import SystemStatus from '../components/SystemStatus';
import healthService from '../services/healthService';

/**
 * Pagina di esempio per testare il servizio MCP Health Check
 * Mostra lo status del sistema e permette di testare varie funzionalità
 */
const SystemHealthPage = () => {
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [consoleReport, setConsoleReport] = useState('');
  const [loading, setLoading] = useState(false);

  const handleGetReport = async () => {
    setLoading(true);
    try {
      const report = await healthService.getStatusReport();
      setConsoleReport(report);
      console.log(report);
    } catch (error) {
      setConsoleReport(`Errore: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleTestDatabase = async () => {
    setLoading(true);
    try {
      const isConnected = await healthService.isMySQLConnected();
      alert(isConnected ? '✅ MySQL è connesso!' : '❌ MySQL non è connesso');
    } catch (error) {
      alert(`Errore: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleTestHealthy = async () => {
    setLoading(true);
    try {
      const isHealthy = await healthService.isHealthy();
      alert(isHealthy ? '✅ Sistema HEALTHY' : '⚠️ Sistema UNHEALTHY');
    } catch (error) {
      alert(`Errore: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box mb={4}>
        <Typography variant="h4" component="h1" gutterBottom>
          <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          System Health Monitor
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Monitoraggio dello status del backend FeathersJS e MySQL
        </Typography>
      </Box>

      {/* Alert informativo */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Questa pagina utilizza il <strong>Health Service MCP</strong> per verificare lo status del sistema.
          Il servizio controlla FeathersJS, MySQL, memoria e altre metriche del backend.
        </Typography>
      </Alert>

      {/* Controlli */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Controlli
        </Typography>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <FormControlLabel
            control={
              <Switch
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
            }
            label="Auto-refresh (30s)"
          />
          <Button
            variant="outlined"
            onClick={handleGetReport}
            disabled={loading}
          >
            Genera Report Console
          </Button>
          <Button
            variant="outlined"
            onClick={handleTestDatabase}
            disabled={loading}
          >
            Test MySQL
          </Button>
          <Button
            variant="outlined"
            onClick={handleTestHealthy}
            disabled={loading}
          >
            Test Healthy
          </Button>
        </Box>
      </Paper>

      {/* Status Component */}
      <Box mb={3}>
        <SystemStatus autoRefresh={autoRefresh} refreshInterval={30000} />
      </Box>

      {/* Console Report */}
      {consoleReport && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box display="flex" alignItems="center" mb={2}>
            <CodeIcon sx={{ mr: 1 }} />
            <Typography variant="h6">
              Console Report
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Box
            component="pre"
            sx={{
              backgroundColor: '#1e1e1e',
              color: '#d4d4d4',
              p: 2,
              borderRadius: 1,
              overflow: 'auto',
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              whiteSpace: 'pre-wrap'
            }}
          >
            {consoleReport}
          </Box>
        </Paper>
      )}

      {/* Documentazione */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Utilizzo del Health Service
        </Typography>
        <Typography variant="body2" paragraph>
          Il servizio MCP Health Check fornisce vari metodi per verificare lo status del sistema:
        </Typography>
        <Box component="ul" sx={{ pl: 2 }}>
          <li>
            <Typography variant="body2">
              <code>healthService.checkStatus()</code> - Verifica status completo
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              <code>healthService.checkDatabase()</code> - Verifica solo MySQL
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              <code>healthService.isHealthy()</code> - Check rapido sistema
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              <code>healthService.isMySQLConnected()</code> - Check rapido MySQL
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              <code>healthService.getStatusReport()</code> - Report formattato
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              <code>healthService.startMonitoring(callback, interval)</code> - Polling automatico
            </Typography>
          </li>
        </Box>
        <Alert severity="success" sx={{ mt: 2 }}>
          <Typography variant="body2">
            Per maggiori dettagli consulta il file <code>src/services/README.md</code>
          </Typography>
        </Alert>
      </Paper>
    </Container>
  );
};

export default SystemHealthPage;
